import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, User as UserIcon, Plus, FileText, Clock, MailOpen, MailWarning,
  Reply, ReplyAll, Forward, Archive, Trash2, X,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import EmailThreadTags from './EmailThreadTags';

// Thread viewer header: subject, CRM link, tags + action buttons.
export default function EmailViewerHeader({
  thread, listThread, isUnread,
  onUnlinkCrm, onOpenLinkPopover, onTagsChange,
  onOpenCompose, onToggleRead, onArchive, onTrash, onClose, onSnoozeOpen,
}) {
  const navigate = useNavigate();
  const t = listThread || thread;
  const canCreateQuote = t?.crm_match?.type === 'entreprise' && t?.crm_match?.id;

  const createQuote = () => {
    if (!canCreateQuote) {
      toast({
        title: 'Rattachez d\'abord l\'email',
        description: 'Pour créer un devis, rattachez ce fil à une entreprise via le bouton « Rattacher à une entreprise ».',
      });
      return;
    }
    navigate('/app/devis', {
      state: {
        prefillCreate: {
          company_id: t.crm_match.id,
          title: thread.messages[0]?.subject || '',
          source_email_thread_id: thread.thread_id,
        },
      },
    });
  };

  return (
    <header className="px-6 py-4 border-b border-[#EFF0F6] flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <h2 className="text-[16px] font-bold text-[#1A1B2E] truncate">
          {thread.messages[0]?.subject || '(Sans sujet)'}
        </h2>
        <div className="text-[11.5px] text-[#8E8FA5] mt-1 flex items-center gap-2 flex-wrap">
          <span>{thread.messages.length} message{thread.messages.length > 1 ? 's' : ''}</span>
          {t?.crm_match ? (
            <>
              <Link
                to={t.crm_match.link}
                data-testid="email-viewer-crm-link"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-semibold bg-[#EEF2FF] text-[#4F46E5] border border-[#C7D2FE] hover:bg-[#E0E7FF]"
              >
                {t.crm_match.type === 'apprenant' ? <UserIcon className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                {' '}{t.crm_match.type === 'entreprise' ? 'Entreprise' : t.crm_match.type === 'prospect' ? 'Prospect' : 'Apprenant'} : {t.crm_match.name}
                {t.crm_match.manual && <span className="opacity-60">(manuel)</span>}
              </Link>
              <button onClick={onUnlinkCrm} data-testid="email-viewer-unlink-crm" className="text-[10px] text-[#8E8FA5] hover:text-[#B91C1C] underline">Détacher</button>
            </>
          ) : t?.counterparty_email ? (
            <button
              onClick={onOpenLinkPopover}
              data-testid="email-viewer-link-btn"
              title={`Rattacher ${t.counterparty_email} à une entreprise / contact`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-semibold bg-[#FFF7ED] text-[#9A3412] border border-[#FED7AA] hover:bg-[#FFEDD5] whitespace-nowrap"
            >
              <Plus className="w-3 h-3" /> Rattacher
            </button>
          ) : null}
        </div>
        <div className="mt-2">
          <EmailThreadTags
            threadId={thread.thread_id}
            tags={(listThread?.tags) || thread.tags || []}
            onChange={onTagsChange}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={createQuote}
          data-testid="email-viewer-create-quote-btn"
          title={canCreateQuote ? 'Créer un devis pré-rempli depuis cet email' : 'Rattachez d\'abord ce fil à une entreprise pour créer un devis'}
          disabled={!canCreateQuote}
          className={`h-9 px-3 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 ${canCreateQuote ? 'border border-[#FED7AA] text-[#C2410C] bg-white hover:bg-[#FFF7ED]' : 'border border-[#EFF0F6] text-[#A0A1B5] bg-white cursor-not-allowed opacity-60'}`}
        >
          <FileText className="w-3.5 h-3.5" /> Devis
        </button>
        <button
          onClick={onSnoozeOpen}
          data-testid="email-viewer-snooze-btn"
          title={thread.snoozed_until ? `Reporté jusqu'au ${new Date(thread.snoozed_until).toLocaleString('fr-FR')}` : 'Reporter ce message à plus tard'}
          className={`h-9 px-3 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 border ${thread.snoozed_until ? 'border-[#FED7AA] text-[#C2410C] bg-[#FFF7ED]' : 'border-[#EFF0F6] text-[#4A4B63] bg-white hover:bg-[#FAFAFE]'}`}
        >
          <Clock className="w-3.5 h-3.5" /> {thread.snoozed_until ? 'Reporté' : 'Reporter'}
        </button>
        <button
          onClick={onToggleRead}
          data-testid="email-viewer-toggle-read"
          title={isUnread ? 'Marquer comme lu (u)' : 'Marquer comme non lu (u)'}
          className="h-9 w-9 rounded-lg border border-[#EFF0F6] text-[#6B6C85] hover:bg-[#FAFAFE] flex items-center justify-center"
        >
          {isUnread ? <MailOpen className="w-3.5 h-3.5" /> : <MailWarning className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => onOpenCompose('reply')} data-testid="email-reply-btn" title="Répondre (r)" className="h-9 px-3 rounded-lg brand-gradient text-white text-[12px] font-semibold flex items-center gap-1.5">
          <Reply className="w-3.5 h-3.5" /> Répondre
        </button>
        <button onClick={() => onOpenCompose('reply_all')} data-testid="email-reply-all-btn" title="Répondre à tous (a)" className="h-9 w-9 rounded-lg border border-[#EFF0F6] text-[#6B6C85] hover:bg-[#FAFAFE] flex items-center justify-center">
          <ReplyAll className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onOpenCompose('forward')} data-testid="email-forward-btn" title="Transférer (f)" className="h-9 w-9 rounded-lg border border-[#EFF0F6] text-[#6B6C85] hover:bg-[#FAFAFE] flex items-center justify-center">
          <Forward className="w-3.5 h-3.5" />
        </button>
        <div className="w-px bg-[#EFF0F6] mx-1" />
        <button onClick={onArchive} data-testid="email-archive-btn" title="Archiver (e)" className="h-9 w-9 rounded-lg border border-[#EFF0F6] text-[#6B6C85] hover:bg-[#FAFAFE] flex items-center justify-center">
          <Archive className="w-3.5 h-3.5" />
        </button>
        <button onClick={onTrash} data-testid="email-trash-btn" title="Mettre à la corbeille (#)" className="h-9 w-9 rounded-lg border border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center justify-center">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={onClose} title="Fermer (Esc)" className="h-9 w-9 rounded-lg border border-[#EFF0F6] text-[#6B6C85] hover:bg-[#FAFAFE] flex items-center justify-center">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </header>
  );
}
